import { useCallback, useEffect, useState } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import { Encounter, Patient, ServiceRequest } from '@medplum/fhirtypes';
import { SYSTEMS, EXTERNAL_CS } from '@/consts/systems.consts';

export const useEncounterOrders = (
  patient?: Patient,
  encounter?: Encounter
) => {
  const medplum = useMedplum();
  const [orders, setOrders] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchOrders = useCallback(async () => {
    if (!encounter?.id || !patient?.id) {
      setOrders([]);
      return;
    }

    // Invalidate cache to ensure fresh data (same pattern as LIMS orders)
    medplum.invalidateSearches('ServiceRequest');

    setLoading(true);
    try {
      // Get orders for this specific encounter (server-side filtering)
      const encounterOrdersBundle = await medplum.search('ServiceRequest', {
        subject: `Patient/${patient.id}`,
        encounter: `Encounter/${encounter.id}`,
        category: `${SYSTEMS.SNOMED}|${EXTERNAL_CS.SNOMED.CODES.LABORATORY_PROCEDURE}`,
        _sort: '-authored',
        _count: '100',
      });
      const encounterOrders =
        encounterOrdersBundle.entry?.map((e) => e.resource as ServiceRequest) ||
        [];

      setOrders(encounterOrders);
    } catch (error) {
      console.error('Error fetching encounter orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [encounter?.id, patient?.id, medplum]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Subscribe to ServiceRequest changes for real-time order updates
  // Only subscribe to genetic test orders (laboratory procedures) for this patient
  useSubscription(
    patient?.id
      ? `ServiceRequest?subject=Patient/${patient.id}&category=${SYSTEMS.SNOMED}|${EXTERNAL_CS.SNOMED.CODES.LABORATORY_PROCEDURE}`
      : '',
    () => {
      // Genetic test order created/updated - refresh the list
      fetchOrders();
    }
  );

  return { orders, loading, refetch: fetchOrders };
};
